import type { ReactNode } from 'react';
import { cn } from '../cn.js';

/** A page section with consistent vertical rhythm + optional heading. */
export interface SectionProps {
  id?: string;
  eyebrow?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  className?: string;
  children?: ReactNode;
}

export function Section({ id, eyebrow, title, subtitle, className, children }: SectionProps) {
  return (
    <section id={id} className={cn('mx-auto max-w-5xl px-6 py-16', className)}>
      {eyebrow ? (
        <p className="mb-2 text-center text-sm font-semibold tracking-wide text-primary">{eyebrow}</p>
      ) : null}
      {title ? (
        <h2 className="text-center text-3xl font-semibold tracking-tight">{title}</h2>
      ) : null}
      {subtitle ? (
        <p className="mx-auto mt-3 max-w-2xl text-center text-muted-foreground">{subtitle}</p>
      ) : null}
      {children ? <div className="mt-10">{children}</div> : null}
    </section>
  );
}

export interface Step {
  title: string;
  description: string;
}

/** Numbered horizontal steps (e.g. the 3-step how-it-works). */
export function Steps({ steps, className }: { steps: Step[]; className?: string }) {
  return (
    <ol className={cn('grid gap-6 md:grid-cols-3', className)}>
      {steps.map((s, i) => (
        <li key={s.title} className="rounded-2xl border border-brand-sage/15 bg-card p-6 shadow-brand">
          <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
            {i + 1}
          </div>
          <h3 className="font-semibold">{s.title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{s.description}</p>
        </li>
      ))}
    </ol>
  );
}

export interface QA {
  q: string;
  a: string;
}

export function FAQ({ items, className }: { items: QA[]; className?: string }) {
  return (
    <div className={cn('mx-auto max-w-2xl divide-y divide-border', className)}>
      {items.map((item) => (
        <details key={item.q} className="group py-4">
          <summary className="flex cursor-pointer items-center justify-between font-medium">
            {item.q}
            <span className="text-muted-foreground transition-transform group-open:rotate-45">+</span>
          </summary>
          <p className="mt-2 text-sm text-muted-foreground">{item.a}</p>
        </details>
      ))}
    </div>
  );
}

export function SocialProof({ items, className }: { items: string[]; className?: string }) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-center gap-x-8 gap-y-3 px-6 text-sm font-medium text-muted-foreground',
        className,
      )}
    >
      {items.map((i) => (
        <span key={i}>{i}</span>
      ))}
    </div>
  );
}

export interface Metric {
  value: string;
  label: string;
}

export function Stats({ metrics, className }: { metrics: Metric[]; className?: string }) {
  return (
    <div className={cn('grid gap-6 sm:grid-cols-3', className)}>
      {metrics.map((m) => (
        <div key={m.label} className="text-center">
          <p className="text-3xl font-semibold tracking-tight text-primary">{m.value}</p>
          <p className="mt-1 text-sm text-muted-foreground">{m.label}</p>
        </div>
      ))}
    </div>
  );
}
